@import "tailwindcss";

:root {
  --space: #0f3575;
  --space-deep: #0a285d;
  --azure: #266bff;
  --azure-dark: #1e54cc;
  --aqua: #8bffff;
  --platinum: #e7ebf0;
  --mist: #c3ccd9;
  --slate: #5b7290;
  --charcoal: #36454f;
  --ink: #1a2230;
  --success: #00b894;
  --warning: #f59e0b;
  --error: #e5484d;
  --white: #ffffff;
  --surface: #f6f8fb;
  --surface-blue: #f1f6ff;
  --shadow: 0 10px 30px rgba(15, 53, 117, 0.08);
  --shadow-strong: 0 22px 60px rgba(15, 53, 117, 0.18);
}

* {
  box-sizing: border-box;
}

html {
  background: var(--surface);
}

body {
  margin: 0;
  min-width: 320px;
  background: var(--surface);
  color: var(--charcoal);
  font-family: var(--font-inter), var(--font-noto-thai), Inter, "Noto Sans Thai", sans-serif;
  font-size: 14px;
  -webkit-font-smoothing: antialiased;
}

button,
input,
select,
textarea {
  font: inherit;
}

button {
  cursor: pointer;
}

button:focus-visible,
input:focus-visible,
select:focus-visible,
textarea:focus-visible {
  outline: 3px solid rgba(38, 107, 255, 0.24);
  outline-offset: 1px;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.app-shell {
  min-height: 100vh;
}

.top-nav {
  position: sticky;
  top: 0;
  z-index: 10;
  min-height: 68px;
  padding: 0 28px;
  display: grid;
  grid-template-columns: auto auto minmax(240px, 1fr) auto auto;
  gap: 24px;
  align-items: center;
  background: var(--space);
  color: white;
  box-shadow: 0 4px 18px rgba(6, 29, 70, 0.16);
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
  font-size: 17px;
  font-weight: 750;
  letter-spacing: -0.02em;
}

.brand-logo {
  position: relative;
  width: 34px;
  height: 34px;
  overflow: hidden;
  border-radius: 9px;
  background: white;
}

.brand-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(2.45);
}

.nav-links {
  display: flex;
  height: 68px;
  gap: 4px;
}

.nav-links button {
  position: relative;
  display: flex;
  align-items: center;
  gap: 7px;
  border: 0;
  padding: 0 13px;
  background: transparent;
  color: rgba(255, 255, 255, 0.68);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.nav-links button:hover,
.nav-links button.active {
  color: white;
}

.nav-links button.active::after {
  content: "";
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 0;
  height: 3px;
  border-radius: 3px 3px 0 0;
  background: var(--aqua);
}

.global-search {
  justify-self: end;
  width: min(100%, 390px);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.1);
}

.global-search input {
  flex: 1;
  height: 38px;
  min-width: 0;
  border: 0;
  outline: 0;
  color: white;
  background: transparent;
}

.global-search input::placeholder {
  color: rgba(255, 255, 255, 0.54);
}

.global-search kbd {
  border-radius: 5px;
  padding: 2px 6px;
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.1);
  font-size: 10px;
}

.nav-bell {
  position: relative;
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 50%;
  color: white;
  background: transparent;
}

.nav-bell span {
  position: absolute;
  top: 6px;
  right: 5px;
  width: 6px;
  height: 6px;
  border: 2px solid var(--space);
  border-radius: 50%;
  background: var(--aqua);
}

.user-chip {
  display: flex;
  align-items: center;
  gap: 9px;
  white-space: nowrap;
}

.user-avatar {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border: 2px solid rgba(139, 255, 255, 0.65);
  border-radius: 50%;
  color: var(--space);
  background: white;
  font-size: 10px;
  font-weight: 800;
}

.user-chip strong,
.user-chip span {
  display: block;
}

.user-chip strong {
  font-size: 12px;
}

.user-chip span {
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.65);
  font-size: 10px;
}

.ward-page {
  max-width: 1380px;
  margin: 0 auto;
  padding: 50px 34px 64px;
}

.ward-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 40px;
  margin-bottom: 28px;
}

.eyebrow {
  display: flex;
  align-items: center;
  gap: 7px;
  color: var(--slate);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.ward-hero h1 {
  margin: 9px 0 10px;
  color: var(--space);
  font-size: clamp(34px, 4vw, 50px);
  letter-spacing: -0.045em;
  line-height: 1;
}

.ward-hero p {
  max-width: 680px;
  margin: 0;
  color: var(--slate);
  font-size: 15px;
  line-height: 1.65;
}

.round-status {
  min-width: 240px;
  padding: 18px 20px;
  border: 1px solid #d5deea;
  border-radius: 16px;
  background: white;
  box-shadow: var(--shadow);
}

.round-status > span {
  color: var(--slate);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.round-status strong {
  display: block;
  margin: 6px 0;
  color: var(--space);
  font-size: 26px;
}

.round-status div {
  height: 6px;
  overflow: hidden;
  border-radius: 999px;
  background: var(--platinum);
}

.round-status i {
  display: block;
  width: 68%;
  height: 100%;
  border-radius: 999px;
  background: var(--azure);
}

.round-status small {
  display: block;
  margin-top: 8px;
  color: var(--slate);
}

.ward-card {
  overflow: hidden;
  border: 1px solid #d3ddea;
  border-radius: 18px;
  background: white;
  box-shadow: var(--shadow);
}

.ward-card-head {
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  border-bottom: 1px solid var(--platinum);
}

.ward-card-head h2 {
  margin: 0 0 4px;
  color: var(--space);
  font-size: 18px;
}

.ward-card-head p {
  margin: 0;
  color: var(--slate);
  font-size: 12px;
}

.ward-card-head > div:last-child {
  display: flex;
  align-items: center;
  gap: 12px;
}

.secure-label {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--slate);
  font-size: 11px;
}

.ward-card-head button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 13px;
  border: 1px solid #b8c7db;
  border-radius: 8px;
  color: var(--space);
  background: white;
  font-size: 12px;
  font-weight: 700;
}

.ward-table-wrap {
  overflow-x: auto;
}

.ward-table {
  width: 100%;
  border-collapse: collapse;
}

.ward-table th {
  padding: 11px 18px;
  color: var(--slate);
  background: #f7f9fc;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-align: left;
  text-transform: uppercase;
}

.ward-table td {
  padding: 13px 18px;
  border-top: 1px solid #edf0f4;
  color: var(--charcoal);
  white-space: nowrap;
}

.ward-table tr.featured-row {
  background: rgba(38, 107, 255, 0.035);
  box-shadow: inset 3px 0 var(--azure);
}

.ward-table tr:hover {
  background: #f8faff;
}

.patient-cell {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 0;
  border: 0;
  color: inherit;
  background: transparent;
  text-align: left;
}

.patient-cell > span {
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border-radius: 11px;
  color: var(--space);
  background: #eaf0fb;
  font-size: 11px;
  font-weight: 800;
}

.patient-cell strong,
.patient-cell small,
.ward-table td > strong,
.ward-table td > small {
  display: block;
}

.patient-cell strong {
  color: var(--ink);
  font-size: 13px;
}

.patient-cell small,
.ward-table td > small {
  margin-top: 3px;
  color: var(--slate);
  font-size: 10px;
}

.ward-table td > strong {
  font-size: 12px;
}

.patient-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}

.patient-status::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.patient-status.stable {
  color: #007e68;
  background: #e5faf5;
}

.patient-status.watch {
  color: #9a6200;
  background: #fff4da;
}

.patient-status.critical {
  color: #b52e34;
  background: #ffeaeb;
}

.open-chart {
  padding: 7px 10px;
  border: 1px solid #bed0ef;
  border-radius: 7px;
  color: var(--azure-dark);
  background: white;
  font-size: 11px;
  font-weight: 750;
}

.open-chart:not(:disabled):hover {
  color: white;
  background: var(--space);
}

.prototype-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin: 18px 0 0;
  color: var(--slate);
  font-size: 12px;
}

.clinical-workspace {
  position: fixed;
  inset: 0;
  z-index: 50;
  overflow: auto;
  background: #f3f6fa;
}

.clinical-workspace > .top-nav {
  position: sticky;
}

.patient-banner {
  position: relative;
  max-width: 1840px;
  min-height: 108px;
  margin: 0 auto;
  padding: 20px 28px;
  display: grid;
  grid-template-columns: minmax(420px, 1.2fr) minmax(340px, 1fr) auto;
  align-items: center;
  gap: 24px;
  border-bottom: 1px solid #cfd8e5;
  background: white;
}

.patient-identity {
  display: flex;
  align-items: center;
  gap: 14px;
}

.patient-avatar {
  width: 48px;
  height: 48px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border: 1px solid #d5deea;
  border-radius: 13px;
  color: var(--space);
  background: #edf3fd;
}

.patient-name-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 4px;
}

.patient-name-row h1 {
  margin: 0;
  color: var(--space);
  font-family: var(--font-noto-thai), var(--font-inter), sans-serif;
  font-size: 21px;
  letter-spacing: -0.02em;
}

.age-pill {
  padding: 4px 7px;
  border-radius: 6px;
  color: var(--charcoal);
  background: var(--platinum);
  font-size: 10px;
  font-weight: 700;
}

.patient-subline {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 7px;
  color: var(--slate);
  font-size: 11px;
}

.patient-subline span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.patient-clinical {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.allergy-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 9px;
  border: 1px solid rgba(229, 72, 77, 0.25);
  border-radius: 7px;
  color: #b82e34;
  background: #fff0f0;
  font-size: 11px;
  font-weight: 750;
}

.diagnosis-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.diagnosis-chip {
  padding: 5px 8px;
  border: 1px solid #c7d8f2;
  border-radius: 6px;
  color: var(--space);
  background: #f5f8fd;
  font-size: 10px;
  font-weight: 800;
}

.mini-stats {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mini-stat {
  min-width: 82px;
  padding: 8px 10px;
  border: 1px solid #d4ddea;
  border-radius: 9px;
  background: #fbfcfe;
}

.mini-stat span {
  display: block;
  color: var(--slate);
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
}

.mini-stat strong {
  display: block;
  margin-top: 2px;
  color: var(--space);
  font-size: 15px;
}

.live-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 7px;
  border-radius: 999px;
  color: #007b65;
  background: #dff9f3;
  font-size: 9px;
  font-weight: 850;
}

.take-order-banner {
  position: absolute;
  left: 0;
  right: 0;
  bottom: -27px;
  z-index: 5;
  height: 27px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--space);
  background: var(--aqua);
  font-size: 11px;
  font-weight: 750;
}

.order-header {
  position: sticky;
  top: 68px;
  z-index: 9;
  max-width: 1840px;
  min-height: 58px;
  margin: 0 auto;
  padding: 9px 28px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #cbd5e2;
  background: rgba(247, 249, 252, 0.96);
  backdrop-filter: blur(16px);
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 8px;
  border: 0;
  color: var(--slate);
  background: transparent;
  font-size: 11px;
  font-weight: 700;
}

.order-header label {
  height: 38px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 9px;
  border: 1px solid #cdd7e4;
  border-radius: 8px;
  color: var(--slate);
  background: white;
}

.order-header label span {
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.order-header input,
.order-header select {
  min-width: 0;
  border: 0;
  outline: 0;
  color: var(--ink);
  background: transparent;
  font-size: 11px;
  font-weight: 650;
}

.take-toggle {
  min-width: 140px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 0 9px 0 11px;
  border: 1px solid var(--space);
  border-radius: 9px;
  color: white;
  background: var(--space);
}

.take-toggle > span {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 750;
}

.take-toggle i {
  position: relative;
  width: 30px;
  height: 18px;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.25);
  transition: background 0.2s ease;
}

.take-toggle b {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: white;
  transition: transform 0.2s ease;
}

.take-toggle.on i {
  background: var(--aqua);
}

.take-toggle.on b {
  transform: translateX(12px);
}

.dictation-control {
  height: 38px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.dictation-control button,
.template-select,
.audit-button {
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 11px;
  border: 1px solid #c8d4e3;
  border-radius: 8px;
  color: var(--space);
  background: white;
  font-size: 10px;
  font-weight: 750;
}

.dictation-control .dictation-start {
  border-color: var(--azure);
  color: white;
  background: var(--azure);
}

.recording-indicator {
  padding: 0 4px;
  color: var(--error);
  font-size: 9px;
  font-weight: 850;
  letter-spacing: 0.04em;
}

.template-select {
  min-width: 112px;
}

.audit-button {
  margin-left: auto;
}

.progress-grid {
  max-width: 1840px;
  margin: 0 auto;
  padding: 18px 28px 28px;
  display: grid;
  grid-template-columns: 280px minmax(620px, 1fr) 320px;
  gap: 18px;
  align-items: start;
}

.left-rail,
.center-column,
.right-rail {
  display: grid;
  gap: 18px;
}

.panel {
  overflow: hidden;
  border: 1px solid #cbd6e3;
  border-radius: 14px;
  background: white;
  box-shadow: 0 4px 12px rgba(15, 53, 117, 0.045);
}

.panel-heading {
  min-height: 48px;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #d2dbe7;
  color: var(--space);
  background: #eef2f7;
}

.panel-heading > div {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 800;
}

.context-chip,
.persona,
.author-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 7px;
  border-radius: 999px;
  color: var(--space);
  background: var(--aqua);
  font-size: 8px;
  font-weight: 850;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.accordion-item {
  border-bottom: 1px solid #edf0f4;
}

.accordion-item:last-child {
  border-bottom: 0;
}

.accordion-trigger {
  position: relative;
  padding: 4px 7px 4px 8px;
  display: flex;
  align-items: center;
}

.accordion-trigger > button:first-child {
  min-height: 43px;
  flex: 1;
  display: grid;
  grid-template-columns: 18px 1fr 16px;
  align-items: center;
  gap: 6px;
  border: 0;
  border-radius: 7px;
  color: var(--slate);
  background: transparent;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
}

.accordion-trigger > button:first-child svg:last-child {
  transition: transform 0.2s ease;
}

.accordion-item.is-open .accordion-trigger > button:first-child {
  color: var(--space);
  background: #eaf1ff;
}

.accordion-item.is-open .accordion-trigger > button:first-child svg:last-child {
  transform: rotate(180deg);
}

.pin-button {
  width: 27px;
  height: 27px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 6px;
  color: var(--slate);
  background: transparent;
}

.pin-button.is-pinned {
  color: var(--space);
  background: var(--aqua);
}

.viewer-detail {
  margin: 0 8px 8px;
  padding: 10px;
  border: 1px solid #d6deea;
  border-radius: 8px;
  background: white;
  animation: reveal 0.18s ease;
}

.result-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  color: var(--space);
  font-size: 10px;
}

.result-head:not(:first-child) {
  margin-top: 8px;
  border-top: 1px solid var(--platinum);
}

.result-head span {
  color: var(--azure-dark);
  font-size: 9px;
  font-weight: 750;
}

.lab-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 5px;
}

.lab-grid > div {
  padding: 6px;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: baseline;
  border-radius: 5px;
  background: #f6f8fb;
}

.lab-grid span {
  color: var(--slate);
  font-size: 9px;
}

.lab-grid strong {
  color: var(--ink);
  font-size: 11px;
}

.lab-grid small {
  grid-column: 1 / -1;
  color: #8594a7;
  font-size: 7px;
}

.lab-grid .abnormal {
  background: #fff0f0;
}

.lab-grid .abnormal strong {
  color: var(--error);
}

.text-link {
  width: 100%;
  margin-top: 9px;
  padding: 4px;
  border: 0;
  color: var(--azure-dark);
  background: transparent;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
}

.vital-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 7px;
}

.vital-summary > div {
  padding: 8px;
  border-radius: 7px;
  background: #f3f6fa;
}

.vital-summary span,
.vital-summary strong,
.vital-summary small {
  display: block;
}

.vital-summary span,
.vital-summary small {
  color: var(--slate);
  font-size: 8px;
}

.vital-summary strong {
  margin: 3px 0 1px;
  color: var(--space);
  font-size: 16px;
}

.sparkline {
  height: 58px;
  margin: 10px 0 5px;
  padding: 6px 5px;
  display: flex;
  align-items: flex-end;
  gap: 6px;
  border-bottom: 1px solid #dce3ec;
}

.sparkline span {
  flex: 1;
  min-height: 8px;
  max-height: 44px;
  border-radius: 4px 4px 1px 1px;
  background: linear-gradient(to top, var(--space), var(--azure));
}

.micro-copy {
  margin: 6px 0 0;
  color: var(--slate);
  font-size: 9px;
  line-height: 1.5;
}

.viewer-list {
  margin: 0 8px 8px;
  padding: 8px 8px 8px 24px;
  color: var(--charcoal);
  font-size: 10px;
  line-height: 1.5;
}

.viewer-list li + li {
  margin-top: 7px;
}

.mar-list li::marker {
  color: var(--azure);
}

.prior-notes article + article {
  margin-top: 9px;
  padding-top: 9px;
  border-top: 1px solid var(--platinum);
}

.prior-notes span {
  color: var(--slate);
  font-size: 8px;
  font-weight: 700;
}

.prior-notes p {
  margin: 4px 0 0;
  color: var(--charcoal);
  font-size: 10px;
  line-height: 1.45;
}

.mission-card {
  padding: 16px;
  border-radius: 13px;
  color: white;
  background: var(--space);
  box-shadow: var(--shadow);
}

.mission-card > span {
  color: var(--aqua);
  font-size: 9px;
  font-weight: 850;
  letter-spacing: 0.08em;
}

.mission-card strong {
  display: block;
  margin: 5px 0 8px;
  font-size: 26px;
}

.mission-card > div {
  height: 5px;
  overflow: hidden;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.18);
}

.mission-card i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--aqua);
}

.mission-card p {
  margin: 10px 0 0;
  color: rgba(255, 255, 255, 0.72);
  font-size: 10px;
  line-height: 1.45;
}

.soap-panel-heading {
  min-height: 52px;
}

.soap-panel-heading > div:last-child {
  gap: 8px;
}

.autosave {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #007e68;
  font-size: 9px;
  font-weight: 700;
}

.soap-grid {
  padding: 14px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.soap-card {
  position: relative;
  min-width: 0;
  padding: 11px;
  border: 1px solid #cfd8e5;
  border-left: 3px solid #cfd8e5;
  border-radius: 10px;
  background: white;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.soap-card.has-suggestion {
  border-left: 3px solid var(--azure);
  background: #fbfdff;
}

.soap-card.is-focused {
  border-color: #8eb3ff;
  border-left-color: var(--azure);
  box-shadow: 0 0 0 3px rgba(38, 107, 255, 0.09);
}

.soap-card-head {
  min-height: 32px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.soap-card-head > div {
  display: flex;
  align-items: flex-start;
  gap: 7px;
}

.soap-letter {
  width: 23px;
  height: 23px;
  display: grid;
  place-items: center;
  border-radius: 6px;
  color: white;
  background: var(--space);
  font-size: 11px;
  font-weight: 850;
}

.soap-card h3 {
  margin: 0;
  color: var(--space);
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.soap-card-head p {
  margin: 2px 0 0;
  color: var(--slate);
  font-size: 8px;
}

.ai-label {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 4px 6px;
  border-radius: 99px;
  color: var(--space);
  background: var(--aqua);
  font-size: 7px;
  font-weight: 850;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.soap-field-shell {
  position: relative;
}

.soap-card textarea {
  width: 100%;
  min-height: 82px;
  max-height: 240px;
  margin-top: 7px;
  padding: 7px 8px;
  resize: none;
  overflow-y: auto;
  border: 1px solid #e0e6ee;
  border-radius: 7px;
  outline: 0;
  color: var(--ink);
  background: white;
  font-size: 12px;
  line-height: 1.55;
}

.soap-card textarea::placeholder {
  color: #a2afbd;
}

.ghost-suggestion {
  position: relative;
  margin: -1px 1px 0;
  padding: 8px 52px 8px 9px;
  display: flex;
  align-items: flex-start;
  min-height: 40px;
  border: 1px dashed #89afff;
  border-top: 0;
  border-radius: 0 0 7px 7px;
  color: rgba(54, 69, 79, 0.45);
  background: #f7faff;
  font-size: 11px;
  font-style: italic;
  line-height: 1.45;
}

.ghost-copy {
  display: inline;
}

.ghost-actions {
  position: absolute;
  top: 7px;
  right: 7px;
  display: flex;
  gap: 3px;
}

.ghost-actions button {
  width: 20px;
  height: 20px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 5px;
  color: var(--space);
  background: #e4ecfa;
}

.ghost-actions button:first-child {
  color: white;
  background: var(--azure);
}

.evidence-tooltip {
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: calc(100% + 8px);
  z-index: 15;
  padding: 10px;
  display: none;
  border-radius: 8px;
  color: white;
  background: var(--space-deep);
  box-shadow: var(--shadow-strong);
  font-size: 9px;
  font-style: normal;
  line-height: 1.4;
}

.evidence-tooltip::after {
  content: "";
  position: absolute;
  left: 16px;
  top: 100%;
  border: 6px solid transparent;
  border-top-color: var(--space-deep);
}

.evidence-tooltip strong,
.evidence-tooltip em {
  display: block;
}

.evidence-tooltip strong {
  color: var(--aqua);
  font-size: 8px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.evidence-tooltip em {
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.72);
}

.ghost-suggestion:hover .evidence-tooltip,
.ghost-suggestion:focus .evidence-tooltip {
  display: block;
}

.convert-card {
  margin-top: 7px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid #b8c9e1;
  border-radius: 7px;
  background: #eef3fb;
}

.convert-card > div {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  color: var(--space);
}

.convert-card strong,
.convert-card small {
  display: block;
}

.convert-card strong {
  font-size: 9px;
}

.convert-card small {
  margin-top: 2px;
  color: var(--slate);
  font-size: 8px;
}

.convert-card button {
  flex: 0 0 auto;
  padding: 7px 8px;
  border: 0;
  border-radius: 6px;
  color: white;
  background: var(--space);
  font-size: 8px;
  font-weight: 850;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.transcribing {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--error);
  font-size: 9px;
  font-weight: 700;
}

.soap-footer {
  padding: 11px 14px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 12px;
  border-top: 1px solid #d6deea;
  background: #eef2f7;
}

.clear-button {
  padding: 8px;
  border: 0;
  color: var(--slate);
  background: transparent;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}

.footer-evidence {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  color: var(--slate);
  font-size: 9px;
}

.finalize-button {
  min-width: 205px;
  padding: 10px 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 0;
  border-radius: 8px;
  color: white;
  background: var(--space);
  box-shadow: 0 6px 15px rgba(15, 53, 117, 0.18);
  font-size: 11px;
  font-weight: 800;
}

.finalize-button:hover {
  background: var(--space-deep);
}

.finalize-button.is-finalized {
  background: #007e68;
}

.order-panel-heading > span {
  color: var(--slate);
  font-size: 9px;
}

.order-entry-tools {
  padding: 12px 14px 10px;
}

.omnibox-row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 7px;
}

.omnibox {
  height: 43px;
  padding: 0 6px 0 11px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #b9c8dc;
  border-radius: 9px;
  color: var(--azure-dark);
  background: #f6f9fd;
  box-shadow: inset 0 0 0 1px rgba(38, 107, 255, 0.05);
}

.omnibox:focus-within {
  border-color: var(--azure);
  box-shadow: 0 0 0 3px rgba(38, 107, 255, 0.1);
}

.omnibox input {
  min-width: 0;
  height: 100%;
  flex: 1;
  border: 0;
  outline: 0;
  color: var(--ink);
  background: transparent;
  font-size: 11px;
}

.omnibox input::placeholder {
  color: #94a3b5;
}

.omnibox button {
  height: 31px;
  padding: 0 11px;
  border: 0;
  border-radius: 6px;
  color: white;
  background: var(--space);
  font-size: 9px;
  font-weight: 800;
}

.smart-button,
.browse-button {
  height: 43px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0 10px;
  border: 1px solid var(--azure);
  border-radius: 8px;
  color: var(--azure-dark);
  background: white;
  font-size: 9px;
  font-weight: 850;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.browse-button {
  border-color: #bac7d8;
  color: var(--space);
}

.quick-categories {
  margin-top: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
}

.quick-categories > span {
  margin-right: 2px;
  color: var(--slate);
  font-size: 8px;
  font-weight: 800;
  text-transform: uppercase;
}

.quick-categories button {
  padding: 5px 7px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #d4dce7;
  border-radius: 6px;
  color: var(--charcoal);
  background: white;
  font-size: 8px;
  font-weight: 700;
}

.order-lanes {
  padding: 0 14px 14px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.order-lane {
  min-width: 0;
  border: 1px solid #d5deea;
  border-radius: 9px;
  background: #fbfcfe;
}

.lane-head {
  min-height: 38px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #d9e0e9;
}

.lane-head > span {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--space);
  font-size: 9px;
  font-weight: 850;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.one-day .lane-head > span svg {
  color: var(--error);
}

.continuous .lane-head > span svg {
  color: var(--azure);
}

.lane-head b {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  display: grid;
  place-items: center;
  border-radius: 99px;
  color: var(--space);
  background: var(--platinum);
  font-size: 9px;
}

.lane-body {
  min-height: 128px;
  padding: 7px;
}

.empty-lane {
  min-height: 112px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #a1adba;
  text-align: center;
}

.empty-lane strong {
  margin-top: 7px;
  color: var(--slate);
  font-size: 10px;
}

.empty-lane span {
  max-width: 220px;
  margin-top: 3px;
  font-size: 8px;
  line-height: 1.4;
}

.order-chip {
  display: grid;
  grid-template-columns: 29px 1fr auto;
  align-items: center;
  gap: 7px;
  padding: 7px;
  border: 1px solid #d6deea;
  border-radius: 8px;
  background: white;
  box-shadow: 0 2px 6px rgba(15, 53, 117, 0.04);
  animation: slideIn 0.24s ease both;
}

.order-chip + .order-chip {
  margin-top: 6px;
}

.order-chip-icon {
  width: 29px;
  height: 29px;
  display: grid;
  place-items: center;
  border-radius: 7px;
  color: var(--azure-dark);
  background: #eaf1ff;
}

.order-chip-copy {
  min-width: 0;
  cursor: text;
}

.order-chip-copy strong,
.order-chip-copy span,
.order-chip-copy em {
  display: block;
}

.order-chip-copy strong {
  overflow: hidden;
  color: var(--ink);
  font-size: 9px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-chip-copy span {
  margin-top: 2px;
  color: var(--slate);
  font-size: 8px;
}

.order-chip-copy em {
  margin-top: 3px;
  color: var(--error);
  font-size: 7px;
}

.order-chip-copy input {
  width: 100%;
  border: 1px solid var(--azure);
  border-radius: 4px;
  color: var(--ink);
  font-size: 9px;
}

.order-chip-actions {
  display: flex;
  gap: 3px;
}

.order-chip-actions button {
  min-width: 22px;
  height: 22px;
  padding: 0 4px;
  display: grid;
  place-items: center;
  border: 1px solid #d9e0e9;
  border-radius: 5px;
  color: var(--slate);
  background: #f8fafc;
  font-size: 6px;
  font-weight: 850;
}

.order-chip-actions button.is-stat {
  border-color: rgba(229, 72, 77, 0.35);
  color: white;
  background: var(--error);
}

.help-heading {
  background: #eef2f7;
}

.chat-messages {
  height: 338px;
  padding: 12px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chat-bubble {
  max-width: 92%;
  padding: 9px 10px;
  border-radius: 9px;
  font-size: 10px;
  line-height: 1.45;
}

.chat-bubble p {
  margin: 0;
}

.chat-bubble.assistant {
  align-self: flex-start;
  color: var(--charcoal);
  background: var(--platinum);
  border-radius: 4px 9px 9px 9px;
}

.chat-bubble.user {
  align-self: flex-end;
  color: white;
  background: var(--space);
  border-radius: 9px 4px 9px 9px;
}

.chat-bubble.is-thinking {
  color: var(--slate);
  font-style: italic;
}

.citation-chip {
  display: inline-block;
  margin-top: 6px;
  padding: 3px 6px;
  border: 1px solid #c4d2e5;
  border-radius: 99px;
  color: var(--space);
  background: white;
  font-size: 7px;
  font-weight: 750;
}

.suggested-question {
  margin: 0 12px 9px auto;
  max-width: calc(100% - 24px);
  padding: 8px 10px;
  display: block;
  border: 0;
  border-radius: 7px;
  color: white;
  background: var(--space);
  font-size: 9px;
  font-weight: 700;
}

.chat-input {
  margin: 0 12px 12px;
  padding: 5px 5px 5px 10px;
  display: flex;
  align-items: center;
  border: 1px solid #cbd6e3;
  border-radius: 9px;
  background: white;
}

.chat-input input {
  min-width: 0;
  height: 30px;
  flex: 1;
  border: 0;
  outline: 0;
  color: var(--ink);
  font-size: 10px;
}

.chat-input button {
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 7px;
  color: white;
  background: var(--azure);
}

.heartbeat-feed .panel-heading {
  background: #eafffb;
}

.feed-count {
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  display: grid;
  place-items: center;
  border-radius: 99px;
  color: var(--space);
  background: var(--aqua);
  font-size: 9px;
  font-weight: 850;
}

.feed-card {
  border-bottom: 1px solid #e4e8ee;
  animation: slideIn 0.25s ease both;
}

.feed-summary {
  width: 100%;
  min-height: 52px;
  padding: 9px 11px;
  display: grid;
  grid-template-columns: 18px 1fr 14px;
  align-items: start;
  gap: 7px;
  border: 0;
  color: var(--charcoal);
  background: white;
  text-align: left;
}

.feed-summary > svg:last-child {
  color: var(--slate);
  transition: transform 0.18s ease;
}

.feed-summary[aria-expanded="true"] > svg:last-child {
  transform: rotate(180deg);
}

.feed-card.warning .feed-summary > svg:first-child {
  color: var(--warning);
}

.feed-card.success .feed-summary > svg:first-child {
  color: var(--success);
}

.feed-card.info .feed-summary > svg:first-child {
  color: var(--azure);
}

.feed-summary strong,
.feed-summary small {
  display: block;
}

.feed-summary strong {
  color: var(--ink);
  font-size: 10px;
}

.feed-summary small {
  margin-top: 3px;
  color: var(--slate);
  font-size: 8px;
  line-height: 1.35;
}

.feed-details {
  padding: 0 11px 10px 36px;
  background: #f9fbfd;
}

.feed-details p {
  margin: 0 0 8px;
  color: var(--charcoal);
  font-size: 8px;
  line-height: 1.45;
}

.feed-details > div {
  display: flex;
  gap: 5px;
}

.feed-details button,
.status-label {
  padding: 5px 7px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #ccd7e4;
  border-radius: 6px;
  color: var(--space);
  background: white;
  font-size: 8px;
  font-weight: 750;
  text-transform: capitalize;
}

.feed-card.is-resolved {
  opacity: 0.58;
}

.feed-footer {
  padding: 9px 11px;
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--slate);
  background: #f4f7fa;
  font-size: 8px;
}

.workspace-footer {
  max-width: 1840px;
  margin: 0 auto;
  padding: 10px 28px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--slate);
  font-size: 9px;
}

.workspace-footer > span,
.workspace-footer > div {
  display: flex;
  align-items: center;
  gap: 5px;
}

.connected-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--success);
  box-shadow: 0 0 0 3px rgba(0, 184, 148, 0.12);
}

.drawer-backdrop,
.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: flex;
  justify-content: flex-end;
  background: rgba(9, 29, 62, 0.32);
  backdrop-filter: blur(3px);
}

.audit-drawer {
  width: min(520px, 94vw);
  height: 100%;
  overflow-y: auto;
  color: var(--charcoal);
  background: white;
  box-shadow: -24px 0 60px rgba(15, 53, 117, 0.18);
  animation: drawerIn 0.24s ease both;
}

.drawer-head {
  padding: 24px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  border-bottom: 1px solid var(--platinum);
}

.drawer-kicker {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--azure-dark);
  font-size: 9px;
  font-weight: 850;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.drawer-head h2 {
  margin: 7px 0 4px;
  color: var(--space);
  font-size: 24px;
}

.drawer-head p {
  margin: 0;
  color: var(--slate);
  font-size: 11px;
}

.drawer-head > button,
.template-head > button {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border: 1px solid #d5deea;
  border-radius: 8px;
  color: var(--slate);
  background: white;
}

.audit-list {
  padding: 20px 24px;
}

.audit-entry {
  display: grid;
  grid-template-columns: 32px 1fr;
  gap: 11px;
}

.audit-entry + .audit-entry {
  margin-top: 10px;
}

.audit-timeline {
  position: relative;
  display: flex;
  justify-content: center;
}

.audit-timeline::after {
  content: "";
  position: absolute;
  top: 28px;
  bottom: -18px;
  width: 1px;
  background: #d9e1eb;
}

.audit-entry:last-child .audit-timeline::after {
  display: none;
}

.audit-timeline span {
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  z-index: 1;
  border-radius: 50%;
  color: white;
  background: var(--space);
  font-size: 9px;
  font-weight: 800;
}

.audit-body {
  padding: 12px;
  border: 1px solid #d8e0ea;
  border-radius: 10px;
  background: #fbfcfe;
}

.audit-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.audit-meta span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--slate);
  font-size: 8px;
}

.audit-meta .take-stamp {
  padding: 3px 5px;
  border-radius: 4px;
  color: var(--space);
  background: var(--aqua);
  font-weight: 850;
}

.audit-body h3 {
  margin: 8px 0 5px;
  color: var(--space);
  font-size: 12px;
}

.audit-body p {
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 5px;
  color: var(--charcoal);
  font-size: 9px;
  line-height: 1.5;
}

.provenance {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 4px 6px;
  border-radius: 5px;
  color: var(--space);
  background: var(--platinum);
  font-size: 8px;
  font-weight: 750;
}

.provenance.ai {
  background: var(--aqua);
}

.modal-backdrop {
  align-items: center;
  justify-content: center;
  padding: 22px;
}

.template-modal {
  width: min(820px, 96vw);
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 16px;
  background: white;
  box-shadow: var(--shadow-strong);
  animation: modalIn 0.2s ease both;
}

.template-head {
  padding: 20px;
  display: grid;
  grid-template-columns: 42px 1fr auto;
  align-items: start;
  gap: 12px;
  border-bottom: 1px solid var(--platinum);
}

.template-mark {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: 11px;
  color: var(--space);
  background: var(--aqua);
}

.template-head span {
  color: var(--azure-dark);
  font-size: 8px;
  font-weight: 850;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.template-head h2 {
  margin: 3px 0;
  color: var(--space);
  font-size: 20px;
}

.template-head p {
  margin: 0;
  color: var(--slate);
  font-size: 10px;
}

.template-loading {
  min-height: 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--slate);
  font-size: 11px;
}

.template-columns {
  padding: 18px 20px 10px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}

.template-columns h3 {
  margin: 0 0 9px;
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--space);
  font-size: 11px;
  text-transform: uppercase;
}

.pending-list {
  display: grid;
  gap: 7px;
}

.pending-item {
  padding: 9px;
  display: grid;
  grid-template-columns: 25px 1fr;
  gap: 8px;
  border: 1px dashed #aebfd6;
  border-radius: 8px;
  background: #f9fbfd;
}

.pending-item > span {
  width: 25px;
  height: 25px;
  display: grid;
  place-items: center;
  border-radius: 6px;
  color: white;
  background: var(--space);
  font-size: 9px;
  font-weight: 850;
}

.pending-item strong {
  color: var(--ink);
  font-size: 10px;
}

.pending-item p {
  margin: 0;
  color: var(--charcoal);
  font-size: 9px;
  line-height: 1.45;
}

.pending-item strong + p {
  margin-top: 2px;
  color: var(--slate);
  font-size: 8px;
}

.pending-item.conflict {
  border-color: rgba(229, 72, 77, 0.6);
  background: #fff4f4;
}

.pending-item.conflict > span {
  background: var(--error);
}

.pending-item em {
  display: block;
  margin-top: 4px;
  color: var(--error);
  font-size: 8px;
}

.template-alert {
  margin: 4px 20px 14px;
  padding: 10px 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border: 1px solid rgba(229, 72, 77, 0.3);
  border-radius: 8px;
  color: var(--error);
  background: #fff1f1;
}

.template-alert strong,
.template-alert span {
  display: block;
}

.template-alert strong {
  font-size: 10px;
}

.template-alert span {
  margin-top: 2px;
  color: #8c3b3e;
  font-size: 8px;
}

.template-actions {
  padding: 12px 20px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  border-top: 1px solid var(--platinum);
}

.primary-button,
.secondary-button {
  padding: 9px 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 800;
}

.primary-button {
  border: 0;
  color: white;
  background: var(--space);
}

.secondary-button {
  border: 1px solid #cbd6e3;
  color: var(--space);
  background: white;
}

.toast {
  position: fixed;
  left: 50%;
  bottom: 28px;
  z-index: 110;
  min-width: 320px;
  max-width: min(620px, calc(100vw - 32px));
  padding: 11px 12px;
  display: grid;
  grid-template-columns: 18px 1fr 22px;
  align-items: center;
  gap: 8px;
  transform: translateX(-50%);
  border: 1px solid rgba(139, 255, 255, 0.5);
  border-radius: 10px;
  color: white;
  background: var(--space);
  box-shadow: var(--shadow-strong);
  font-size: 10px;
  font-weight: 700;
  animation: toastIn 0.24s ease both;
}

.toast > svg:first-child {
  color: var(--aqua);
}

.toast button {
  width: 22px;
  height: 22px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 5px;
  color: white;
  background: rgba(255, 255, 255, 0.14);
}

.spin {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes reveal {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes slideIn {
  from { opacity: 0; transform: translateY(7px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes drawerIn {
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes modalIn {
  from { opacity: 0; transform: translateY(12px) scale(0.985); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes toastIn {
  from { opacity: 0; transform: translate(-50%, 10px); }
  to { opacity: 1; transform: translate(-50%, 0); }
}

@media (max-width: 1380px) {
  .top-nav {
    grid-template-columns: auto auto minmax(180px, 1fr) auto;
    gap: 14px;
  }

  .nav-bell {
    display: none;
  }

  .user-chip > div:last-child {
    display: none;
  }

  .progress-grid {
    grid-template-columns: 245px minmax(560px, 1fr) 286px;
    padding-inline: 18px;
  }

  .patient-banner,
  .order-header,
  .workspace-footer {
    padding-inline: 18px;
  }

  .order-header label span {
    display: none;
  }

  .audit-button {
    width: 38px;
    overflow: hidden;
    padding: 0;
    font-size: 0;
  }
}

@media (max-width: 1120px) {
  .nav-links {
    display: none;
  }

  .top-nav {
    grid-template-columns: auto 1fr auto;
  }

  .global-search {
    justify-self: stretch;
  }

  .patient-banner {
    grid-template-columns: 1fr auto;
  }

  .patient-clinical {
    grid-column: 1;
  }

  .mini-stats {
    grid-row: 1 / span 2;
    grid-column: 2;
  }

  .order-header {
    flex-wrap: wrap;
    position: relative;
    top: 0;
  }

  .audit-button {
    margin-left: 0;
  }

  .progress-grid {
    grid-template-columns: 235px 1fr;
  }

  .right-rail {
    grid-column: 1 / -1;
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 840px) {
  .top-nav {
    padding: 0 14px;
  }

  .brand span,
  .global-search kbd {
    display: none;
  }

  .ward-page {
    padding: 30px 16px 50px;
  }

  .ward-hero {
    align-items: stretch;
    flex-direction: column;
  }

  .round-status {
    width: 100%;
  }

  .ward-card-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .patient-banner {
    padding: 15px;
    grid-template-columns: 1fr;
  }

  .patient-clinical,
  .mini-stats {
    grid-column: 1;
    grid-row: auto;
  }

  .order-header {
    padding: 9px 14px;
  }

  .order-header label {
    flex: 1 1 180px;
  }

  .take-toggle {
    flex: 1 1 150px;
  }

  .progress-grid {
    padding: 14px;
    grid-template-columns: 1fr;
  }

  .left-rail,
  .right-rail {
    grid-column: 1;
    grid-template-columns: 1fr;
  }

  .left-rail .mission-card {
    display: none;
  }

  .soap-grid,
  .order-lanes,
  .template-columns {
    grid-template-columns: 1fr;
  }

  .omnibox-row {
    grid-template-columns: 1fr 1fr;
  }

  .omnibox {
    grid-column: 1 / -1;
  }

  .chat-messages {
    height: 270px;
  }

  .workspace-footer {
    padding: 10px 14px 18px;
    align-items: flex-start;
    flex-direction: column;
    gap: 7px;
  }
}

@media (max-width: 560px) {
  .global-search {
    display: none;
  }

  .top-nav {
    grid-template-columns: 1fr auto;
  }

  .patient-subline {
    flex-direction: column;
    gap: 4px;
  }

  .mini-stats {
    flex-wrap: wrap;
  }

  .dictation-control {
    flex-wrap: wrap;
    height: auto;
  }

  .soap-footer {
    grid-template-columns: 1fr;
  }

  .footer-evidence {
    order: -1;
  }

  .finalize-button {
    width: 100%;
  }

  .template-modal {
    max-height: 94vh;
  }

  .template-head {
    grid-template-columns: 36px 1fr auto;
  }

  .template-mark {
    width: 36px;
    height: 36px;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.001ms !important;
  }
}

/* Readability and clinical-workflow revision */
body {
  font-family: var(--font-inter), var(--font-sarabun), Inter, Sarabun, sans-serif;
  font-size: 15px;
  line-height: 1.45;
}

.patient-name-row h1 {
  font-family: var(--font-sarabun), var(--font-inter), Sarabun, sans-serif;
  font-size: 27px;
  font-weight: 700;
  line-height: 1.25;
}

.patient-banner {
  min-height: 126px;
  grid-template-columns: minmax(390px, 1.2fr) minmax(330px, 1fr) auto;
}

.patient-banner .eyebrow,
.patient-subline,
.allergy-chip,
.diagnosis-chip {
  font-size: 12px;
}

.age-pill {
  padding: 5px 9px;
  font-size: 12px;
}

.diagnosis-chip {
  min-width: 48px;
  padding: 6px 10px;
  text-align: center;
}

.mini-stat span,
.live-chip {
  font-size: 10px;
}

.mini-stat strong {
  font-size: 17px;
}

.order-header {
  min-height: 68px;
  row-gap: 8px;
}

.order-header > * {
  flex-shrink: 0;
}

.back-button,
.order-header input,
.order-header select,
.take-toggle > span,
.dictation-control button,
.template-select,
.audit-button {
  font-size: 12px;
}

.order-header label span {
  display: inline;
  font-size: 10px;
}

.audit-button {
  width: auto;
  min-width: 132px;
  overflow: visible;
  padding: 0 11px;
}

.take-toggle {
  min-width: 180px;
}

.take-order-explainer {
  max-width: 210px;
  display: flex;
  align-items: center;
  gap: 7px;
  color: var(--charcoal);
  font-size: 11px;
  line-height: 1.25;
}

.take-order-explainer svg {
  flex: 0 0 auto;
  color: var(--azure-dark);
}

.take-order-explainer strong {
  display: block;
  color: var(--space);
  font-size: 11px;
}

.dictation-processing {
  color: var(--space) !important;
  background: #eef3fb !important;
}

.recording-indicator {
  font-size: 10px;
}

.progress-grid {
  grid-template-columns: 330px minmax(680px, 1fr) 350px;
  gap: 20px;
  padding-top: 20px;
}

.progress-grid.viewer-hidden {
  grid-template-columns: 64px minmax(680px, 1fr) 350px;
}

.left-rail,
.center-column,
.right-rail {
  gap: 20px;
}

.left-rail.is-collapsed {
  align-self: stretch;
}

.panel {
  border-radius: 16px;
}

.panel-heading {
  min-height: 56px;
  padding: 0 16px;
}

.panel-heading > div {
  font-size: 15px;
}

.viewer-heading > div {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 800;
}

.panel-collapse-button,
.section-toggle {
  min-height: 32px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid #c5d0df;
  border-radius: 8px;
  color: var(--space);
  background: white;
  font-size: 11px;
  font-weight: 750;
}

.clinical-viewer.is-collapsed {
  min-height: 520px;
  display: flex;
  align-items: stretch;
}

.viewer-restore {
  width: 100%;
  min-height: 100%;
  padding: 18px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  border: 0;
  color: var(--space);
  background: #eef3fb;
  font-size: 12px;
  font-weight: 800;
}

.viewer-restore span {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
}

.viewer-toolbar {
  padding: 12px;
  display: grid;
  gap: 8px;
  border-bottom: 1px solid var(--platinum);
  background: #fbfcfe;
}

.viewer-toolbar > div {
  height: 38px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  gap: 7px;
  border: 1px solid #c9d4e2;
  border-radius: 8px;
  color: var(--slate);
  background: white;
}

.viewer-toolbar input {
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  color: var(--ink);
  background: transparent;
  font-size: 12px;
}

.viewer-toolbar > span {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--slate);
  font-size: 10px;
}

.accordion-trigger {
  padding: 5px 8px;
}

.accordion-trigger > button:first-child {
  min-height: 48px;
  grid-template-columns: 20px minmax(0, 1fr) auto 18px;
  gap: 7px;
  padding: 0 8px;
  font-size: 12px;
}

.accordion-trigger > button:first-child em {
  padding: 3px 6px;
  border-radius: 99px;
  color: #a9252b;
  background: #fff0f0;
  font-size: 9px;
  font-style: normal;
  font-weight: 800;
}

.pin-button {
  width: auto;
  min-width: 54px;
  height: 30px;
  padding: 0 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 9px;
  font-weight: 750;
}

.viewer-detail {
  margin: 0 10px 10px;
  padding: 12px;
  border-radius: 10px;
}

.clinical-context-alert,
.pacs-summary,
.history-note {
  margin-bottom: 10px;
  padding: 10px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border-radius: 8px;
  font-size: 11px;
}

.clinical-context-alert {
  color: #9b281f;
  background: #fff2ed;
}

.clinical-context-alert strong,
.clinical-context-alert span,
.pacs-summary strong,
.pacs-summary small {
  display: block;
}

.clinical-context-alert span,
.pacs-summary small {
  margin-top: 2px;
  color: var(--slate);
  font-size: 10px;
}

.result-head {
  font-size: 12px;
}

.result-head span {
  color: var(--slate);
  font-size: 10px;
}

.lab-grid span,
.lab-grid strong {
  font-size: 11px;
}

.lab-grid small {
  font-size: 9px;
}

.lab-grid > div {
  min-height: 49px;
  padding: 8px;
}

.text-link,
.micro-copy,
.prior-notes span {
  font-size: 10px;
}

.vital-summary span,
.vital-summary small {
  font-size: 10px;
}

.vital-summary strong {
  font-size: 18px;
}

.condition-list {
  display: grid;
  gap: 8px;
}

.condition-list article {
  padding: 9px;
  display: grid;
  grid-template-columns: 52px 1fr;
  align-items: center;
  gap: 9px;
  border: 1px solid #d6deea;
  border-radius: 8px;
}

.condition-list article > span {
  padding: 5px 6px;
  border-radius: 6px;
  color: var(--space);
  background: #eaf1ff;
  font-size: 11px;
  font-weight: 850;
  text-align: center;
}

.condition-list strong,
.condition-list small {
  display: block;
}

.condition-list strong {
  color: var(--ink);
  font-size: 12px;
}

.condition-list small {
  margin-top: 2px;
  color: var(--slate);
  font-size: 9px;
}

.history-note {
  margin: 10px 0 0;
  color: var(--charcoal);
  background: #f4f7fb;
}

.history-note p {
  margin: 0;
  font-size: 11px;
  line-height: 1.5;
}

.prior-notes p {
  font-size: 11px;
  line-height: 1.55;
}

.prior-notes button {
  margin-top: 7px;
  padding: 5px 7px;
  border: 1px solid #c9d5e5;
  border-radius: 6px;
  color: var(--space);
  background: white;
  font-size: 9px;
  font-weight: 750;
}

.mar-timeline {
  display: grid;
  gap: 8px;
}

.mar-timeline article {
  display: grid;
  grid-template-columns: 24px 1fr;
  align-items: center;
  gap: 7px;
}

.mar-timeline article > span {
  width: 24px;
  height: 24px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: #007e68;
  background: #def8f2;
}

.mar-timeline article > span.pending {
  color: #9a6200;
  background: #fff2cf;
}

.mar-timeline strong,
.mar-timeline small {
  display: block;
}

.mar-timeline strong {
  color: var(--ink);
  font-size: 11px;
}

.mar-timeline small {
  color: var(--slate);
  font-size: 9px;
}

.pacs-summary {
  color: var(--space);
  background: #edf3fd;
}

.attachment-grid {
  display: grid;
  gap: 9px;
}

.attachment-card {
  width: 100%;
  padding: 8px;
  display: grid;
  grid-template-columns: 82px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  border: 1px solid #d2dce9;
  border-radius: 9px;
  color: var(--charcoal);
  background: white;
  text-align: left;
}

.attachment-card:hover {
  border-color: var(--azure);
  box-shadow: 0 5px 15px rgba(38, 107, 255, 0.1);
}

.attachment-card > span strong,
.attachment-card > span small,
.attachment-card > span em {
  display: block;
}

.attachment-card > span strong {
  color: var(--ink);
  font-size: 11px;
}

.attachment-card > span small {
  margin-top: 3px;
  color: var(--slate);
  font-size: 9px;
}

.attachment-card > span em {
  margin-top: 5px;
  color: #007e68;
  font-size: 8px;
  font-style: normal;
  font-weight: 800;
  text-transform: uppercase;
}

.attachment-thumb {
  position: relative;
  height: 66px;
  overflow: hidden;
  border-radius: 7px;
  background: #0c1623;
}

.xray-thumb {
  background: radial-gradient(circle at 50% 40%, #9fb3c8 0 8%, #263b50 9% 53%, #101b28 54% 100%);
}

.xray-lung {
  position: absolute;
  top: 15px;
  width: 27px;
  height: 39px;
  border-radius: 45% 45% 55% 55%;
  background: rgba(4, 12, 22, 0.72);
  box-shadow: inset 0 0 8px rgba(204, 230, 244, 0.15);
}

.xray-lung.left {
  left: 10px;
}

.xray-lung.right {
  right: 10px;
}

.xray-spine {
  position: absolute;
  z-index: 2;
  top: 9px;
  left: 50%;
  width: 4px;
  height: 49px;
  border-radius: 4px;
  background: rgba(218, 233, 245, 0.45);
}

.xray-thumb small {
  position: absolute;
  right: 4px;
  top: 3px;
  z-index: 3;
  color: white;
  font-size: 7px;
}

.ecg-thumb {
  display: grid;
  place-items: center;
  color: #007e68;
  background:
    linear-gradient(rgba(229, 72, 77, 0.11) 1px, transparent 1px),
    linear-gradient(90deg, rgba(229, 72, 77, 0.11) 1px, transparent 1px),
    #fff8f8;
  background-size: 8px 8px;
}

.ecg-thumb span {
  position: absolute;
  right: 5px;
  bottom: 4px;
  color: var(--slate);
  font-size: 7px;
}

.document-thumb {
  padding: 7px 9px;
  color: var(--space);
  background: #eef2f7;
}

.document-thumb i {
  width: 34px;
  height: 3px;
  margin-top: 3px;
  display: block;
  background: #b8c4d3;
}

.document-thumb span {
  position: absolute;
  right: 6px;
  bottom: 5px;
  color: #a9252b;
  font-size: 8px;
  font-weight: 850;
}

.pinned-context {
  padding: 10px;
  border-bottom: 1px solid #d8e1ec;
  background: #fffdec;
}

.pinned-context-head {
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 5px;
  color: #795300;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
}

.pinned-context-card {
  overflow: hidden;
  border: 1px solid #ead58a;
  border-radius: 9px;
  background: white;
}

.pinned-context-card + .pinned-context-card {
  margin-top: 8px;
}

.pinned-context-card > div:first-child {
  min-height: 34px;
  padding: 0 9px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--space);
  font-size: 10px;
  background: #fff8d9;
}

.pinned-context-card > div:first-child button {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  color: #795300;
  background: transparent;
  font-size: 9px;
}

.pinned-context-card .viewer-detail {
  max-height: 250px;
  overflow: auto;
}

.attachment-lightbox {
  position: fixed;
  inset: 0;
  z-index: 100;
  padding: 30px;
  display: grid;
  place-items: center;
  background: rgba(8, 24, 48, 0.72);
  backdrop-filter: blur(4px);
}

.attachment-preview {
  width: min(100%, 980px);
  max-height: 90vh;
  overflow: auto;
  border-radius: 18px;
  background: white;
  box-shadow: 0 28px 80px rgba(4, 20, 45, 0.35);
}

.attachment-preview-head {
  min-height: 78px;
  padding: 15px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
  background: var(--space);
}

.attachment-preview-head span {
  color: var(--aqua);
  font-size: 9px;
  font-weight: 850;
  letter-spacing: 0.08em;
}

.attachment-preview-head h2 {
  margin: 3px 0 0;
  font-size: 20px;
}

.attachment-preview-head button {
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 9px;
  color: white;
  background: transparent;
}

.attachment-preview-body {
  padding: 20px;
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(280px, 0.7fr);
  gap: 22px;
}

.large-clinical-preview {
  min-height: 440px;
  padding: 18px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: #0b1724;
}

.large-clinical-preview .attachment-thumb {
  width: min(100%, 480px);
  height: 380px;
}

.large-clinical-preview.document {
  grid-template-columns: 170px 1fr;
  align-items: start;
  gap: 20px;
  color: var(--ink);
  background: #eef2f7;
}

.large-clinical-preview.document .attachment-thumb {
  width: 170px;
  height: 230px;
}

.mock-document-copy {
  min-height: 360px;
  padding: 30px;
  background: white;
  box-shadow: 0 8px 22px rgba(15, 53, 117, 0.12);
}

.mock-document-copy p {
  font-size: 13px;
  line-height: 1.65;
}

.mock-document-copy i {
  width: 100%;
  height: 5px;
  margin-top: 12px;
  display: block;
  background: #dce3eb;
}

.attachment-preview-body aside h3 {
  margin: 18px 0 7px;
  color: var(--space);
  font-size: 14px;
}

.attachment-preview-body aside p {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
}

.file-status {
  padding: 5px 8px;
  border-radius: 99px;
  color: #007e68;
  background: #def8f2;
  font-size: 10px;
  font-weight: 850;
}

.attachment-preview dl {
  margin: 18px 0;
  border-top: 1px solid var(--platinum);
}

.attachment-preview dl div {
  padding: 9px 0;
  display: grid;
  grid-template-columns: 90px 1fr;
  gap: 8px;
  border-bottom: 1px solid var(--platinum);
  font-size: 11px;
}

.attachment-preview dt {
  color: var(--slate);
}

.attachment-preview dd {
  margin: 0;
  color: var(--ink);
  font-weight: 650;
}

.attachment-actions {
  display: grid;
  gap: 7px;
}

.attachment-actions button {
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid var(--space);
  border-radius: 8px;
  color: white;
  background: var(--space);
  font-size: 11px;
  font-weight: 750;
}

.attachment-actions button:last-child {
  color: var(--space);
  background: white;
}

.soap-panel-heading,
.order-panel-heading {
  min-height: 60px;
}

.soap-panel-heading > div:last-child,
.order-panel-heading > div:last-child {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.autosave,
.author-chip {
  font-size: 10px;
}

.soap-grid {
  padding: 16px;
  gap: 14px;
}

.soap-card {
  padding: 14px;
  border-radius: 11px;
}

.soap-card-head {
  min-height: 40px;
}

.soap-letter {
  width: 28px;
  height: 28px;
  font-size: 13px;
}

.soap-card h3 {
  font-size: 13px;
}

.soap-card-head p {
  margin-top: 3px;
  font-size: 10px;
}

.ai-label {
  font-size: 9px;
}

.soap-card textarea {
  min-height: 112px;
  padding: 10px;
  font-size: 14px;
  line-height: 1.6;
}

.ghost-suggestion {
  min-height: 54px;
  padding: 10px 60px 10px 11px;
  font-size: 13px;
  line-height: 1.55;
}

.ghost-actions button {
  width: 25px;
  height: 25px;
}

.evidence-tooltip {
  font-size: 11px;
}

.evidence-tooltip strong {
  font-size: 9px;
}

.convert-card {
  padding: 10px;
}

.convert-card strong {
  font-size: 11px;
}

.convert-card small,
.convert-card button {
  font-size: 9px;
}

.dictation-session-banner {
  margin: 14px 16px 0;
  padding: 11px 13px;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid rgba(229, 72, 77, 0.28);
  border-radius: 10px;
  color: #9c292e;
  background: #fff1f1;
}

.dictation-session-banner.processing {
  border-color: #bdcce2;
  color: var(--space);
  background: #f0f5fc;
}

.dictation-session-banner > div {
  flex: 1;
}

.dictation-session-banner strong,
.dictation-session-banner span {
  display: block;
}

.dictation-session-banner strong {
  font-size: 12px;
}

.dictation-session-banner span {
  color: var(--charcoal);
  font-size: 10px;
}

.dictation-wave {
  height: 24px;
  display: flex !important;
  align-items: center;
  gap: 3px;
}

.dictation-wave i {
  width: 3px;
  border-radius: 3px;
  background: var(--error);
  animation: audioWave 0.8s ease-in-out infinite alternate;
}

.dictation-wave i:nth-child(1),
.dictation-wave i:nth-child(5) {
  height: 7px;
}

.dictation-wave i:nth-child(2),
.dictation-wave i:nth-child(4) {
  height: 15px;
  animation-delay: 0.15s;
}

.dictation-wave i:nth-child(3) {
  height: 23px;
  animation-delay: 0.3s;
}

@keyframes audioWave {
  to { transform: scaleY(0.45); }
}

.soap-footer {
  padding: 13px 16px;
}

.clear-button,
.footer-evidence,
.finalize-button {
  font-size: 12px;
}

.order-panel-heading > div:last-child > span {
  color: var(--slate);
  font-size: 11px;
  font-weight: 600;
}

.order-entry-tools {
  padding: 14px 16px 12px;
}

.order-routing {
  margin-bottom: 12px;
  padding: 11px;
  display: grid;
  grid-template-columns: minmax(390px, 1fr) minmax(250px, 0.7fr);
  gap: 12px;
  align-items: center;
  border: 1px solid #cad6e6;
  border-radius: 10px;
  background: #f7f9fc;
}

.routing-label {
  display: block;
  margin-bottom: 6px;
  color: var(--space);
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.lane-selector {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 5px;
}

.lane-selector button {
  min-height: 36px;
  padding: 5px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: 1px solid #cbd6e3;
  border-radius: 7px;
  color: var(--charcoal);
  background: white;
  font-size: 10px;
  font-weight: 750;
}

.lane-selector button.active {
  border-color: var(--space);
  color: white;
  background: var(--space);
}

.order-routing p {
  margin: 0;
  padding-left: 12px;
  display: flex;
  align-items: flex-start;
  gap: 7px;
  border-left: 1px solid #d8e0ea;
  color: var(--charcoal);
  font-size: 11px;
  line-height: 1.45;
}

.order-routing p svg {
  flex: 0 0 auto;
  margin-top: 1px;
  color: var(--azure-dark);
}

.omnibox {
  height: 48px;
}

.omnibox input {
  font-size: 13px;
}

.omnibox button,
.smart-button,
.browse-button {
  font-size: 10px;
}

.smart-button,
.browse-button {
  height: 48px;
}

.quick-categories {
  margin-top: 10px;
  gap: 7px;
}

.quick-categories > span,
.quick-categories button {
  font-size: 10px;
}

.lane-head {
  min-height: 45px;
}

.lane-head > span,
.lane-head b {
  font-size: 11px;
}

.lane-body {
  min-height: 158px;
  padding: 9px;
}

.empty-lane {
  min-height: 138px;
}

.empty-lane strong {
  font-size: 12px;
}

.empty-lane span {
  font-size: 10px;
}

.order-chip {
  grid-template-columns: 34px minmax(0, 1fr) auto;
  align-items: start;
  padding: 9px;
}

.order-chip-icon {
  width: 34px;
  height: 34px;
}

.editable-order-name,
.order-usage {
  width: 100%;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 5px;
  border: 0;
  color: var(--ink);
  background: transparent;
  text-align: left;
}

.order-chip-copy .editable-order-name strong {
  font-size: 11px;
}

.order-usage {
  margin-top: 5px;
  flex-wrap: wrap;
}

.order-chip-copy .order-usage span {
  margin: 0;
  padding: 2px 5px;
  border-radius: 4px;
  color: var(--space);
  background: #eaf1ff;
  font-size: 8px;
  font-weight: 850;
  text-transform: uppercase;
}

.order-chip-copy .order-usage strong {
  white-space: normal;
  font-size: 10px;
}

.order-chip-meta {
  margin-top: 7px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 9px;
}

.order-chip-copy .order-chip-meta span {
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--slate);
  font-size: 8px;
}

.order-chip-copy input {
  min-height: 30px;
  padding: 4px 6px;
  font-size: 11px;
}

.order-chip-actions button {
  min-width: 27px;
  height: 27px;
  font-size: 7px;
}

.chat-bubble,
.chat-input input {
  font-size: 12px;
}

.citation-chip,
.suggested-question {
  font-size: 10px;
}

.feed-summary strong {
  font-size: 12px;
}

.feed-summary small,
.feed-detail p {
  font-size: 10px;
}

.ward-table,
.ward-card-head p,
.prototype-hint {
  font-size: 13px;
}

.patient-cell strong {
  font-size: 15px;
}

.patient-cell small,
.ward-table td small {
  font-size: 11px;
}

@media (max-width: 1540px) {
  .order-header {
    flex-wrap: wrap;
  }

  .audit-button {
    margin-left: 0;
  }

  .progress-grid {
    grid-template-columns: 305px minmax(650px, 1fr) 325px;
  }

  .progress-grid.viewer-hidden {
    grid-template-columns: 64px minmax(650px, 1fr) 325px;
  }

  .take-order-explainer {
    display: none;
  }
}

@media (max-width: 1280px) {
  .progress-grid,
  .progress-grid.viewer-hidden {
    grid-template-columns: 300px minmax(0, 1fr);
  }

  .progress-grid.viewer-hidden {
    grid-template-columns: 64px minmax(0, 1fr);
  }

  .right-rail {
    grid-column: 1 / -1;
    grid-template-columns: 1fr 1fr;
  }

  .order-routing {
    grid-template-columns: 1fr;
  }

  .order-routing p {
    padding: 9px 0 0;
    border-top: 1px solid #d8e0ea;
    border-left: 0;
  }
}

@media (max-width: 900px) {
  .patient-banner {
    grid-template-columns: 1fr auto;
  }

  .patient-clinical {
    grid-column: 1;
  }

  .progress-grid,
  .progress-grid.viewer-hidden {
    grid-template-columns: 1fr;
  }

  .left-rail,
  .left-rail.is-collapsed,
  .right-rail {
    grid-column: 1;
  }

  .clinical-viewer.is-collapsed {
    min-height: 54px;
  }

  .viewer-restore {
    min-height: 54px;
    flex-direction: row;
    justify-content: center;
  }

  .viewer-restore span {
    writing-mode: initial;
    transform: none;
  }

  .attachment-preview-body {
    grid-template-columns: 1fr;
  }

  .large-clinical-preview {
    min-height: 340px;
  }

  .soap-grid,
  .order-lanes {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 620px) {
  body {
    font-size: 14px;
  }

  .patient-name-row h1 {
    font-size: 23px;
  }

  .patient-banner {
    grid-template-columns: 1fr;
  }

  .mini-stats {
    grid-column: 1;
  }

  .panel-heading,
  .soap-panel-heading,
  .order-panel-heading {
    min-height: 58px;
    padding: 9px 12px;
    align-items: flex-start;
    gap: 8px;
  }

  .soap-panel-heading,
  .order-panel-heading {
    flex-direction: column;
  }

  .soap-panel-heading > div:last-child,
  .order-panel-heading > div:last-child {
    width: 100%;
    justify-content: flex-start;
  }

  .lane-selector {
    grid-template-columns: 1fr;
  }

  .omnibox-row {
    grid-template-columns: 1fr;
  }

  .omnibox {
    grid-column: 1;
  }

  .smart-button,
  .browse-button {
    justify-content: center;
  }

  .attachment-lightbox {
    padding: 10px;
  }

  .large-clinical-preview.document {
    grid-template-columns: 1fr;
  }

  .large-clinical-preview.document .attachment-thumb {
    width: 100%;
  }

  .order-chip {
    grid-template-columns: 32px minmax(0, 1fr);
  }

  .order-chip-actions {
    grid-column: 2;
    justify-content: flex-start;
  }
}

/* Agent-connected dictation, audit provenance, and Heartbeat actions */
.omnibox-row {
  grid-template-columns: minmax(320px, 1fr) auto auto auto;
}

.order-dictation-button {
  height: 48px;
  padding: 0 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid var(--space);
  border-radius: 8px;
  color: white;
  background: var(--space);
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.order-dictation-recording {
  min-height: 58px;
  margin-bottom: 11px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid rgba(229, 72, 77, 0.32);
  border-radius: 10px;
  color: #9c292e;
  background: #fff1f1;
}

.order-dictation-recording.processing {
  border-color: #c5d2e3;
  color: var(--space);
  background: #f1f5fb;
}

.recording-dot {
  width: 10px;
  height: 10px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: var(--error);
  box-shadow: 0 0 0 5px rgba(229, 72, 77, 0.13);
}

.order-dictation-recording > div {
  min-width: 0;
  flex: 1;
}

.order-dictation-recording strong,
.order-dictation-recording small {
  display: block;
}

.order-dictation-recording strong {
  font-size: 12px;
}

.order-dictation-recording small {
  margin-top: 2px;
  color: var(--charcoal);
  font-size: 10px;
}

.order-dictation-recording > button {
  min-height: 34px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 0;
  border-radius: 7px;
  color: white;
  background: var(--error);
  font-size: 10px;
  font-weight: 800;
}

.order-dictation-review {
  margin-bottom: 12px;
  padding: 13px;
  border: 1px solid #9fbaf0;
  border-left: 4px solid var(--azure);
  border-radius: 11px;
  background: #f7faff;
  box-shadow: 0 7px 22px rgba(38, 107, 255, 0.08);
}

.dictation-review-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.dictation-review-head > div {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: var(--space);
}

.dictation-review-head strong,
.dictation-review-head small {
  display: block;
}

.dictation-review-head strong {
  font-size: 13px;
}

.dictation-review-head small {
  margin-top: 2px;
  color: var(--slate);
  font-size: 10px;
}

.agent-result-mode,
.agent-state {
  padding: 4px 7px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  border-radius: 99px;
  font-size: 8px;
  font-weight: 850;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.agent-result-mode.live,
.agent-state.live {
  color: #006b59;
  background: #dff9f3;
}

.agent-result-mode.simulation,
.agent-state.simulation {
  color: #6b5700;
  background: #fff1bf;
}

.dictation-source {
  margin-top: 11px;
  padding: 9px 10px;
  border-radius: 8px;
  background: #eef2f7;
}

.dictation-source > span,
.order-dictation-review label > span {
  display: block;
  color: var(--space);
  font-size: 9px;
  font-weight: 850;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.dictation-source p {
  margin: 4px 0 0;
  color: var(--charcoal);
  font-size: 11px;
  line-height: 1.5;
}

.order-dictation-review label {
  margin-top: 10px;
  display: block;
}

.order-dictation-review textarea {
  width: 100%;
  margin-top: 6px;
  padding: 9px 10px;
  resize: vertical;
  border: 1px solid #9fb5d5;
  border-radius: 8px;
  outline: 0;
  color: var(--ink);
  background: white;
  font-size: 12px;
  line-height: 1.55;
}

.order-dictation-review textarea:focus {
  border-color: var(--azure);
  box-shadow: 0 0 0 3px rgba(38, 107, 255, 0.1);
}

.dictation-review-note {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--slate);
  font-size: 9px;
}

.dictation-review-actions {
  margin-top: 11px;
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 6px;
}

.dictation-review-actions button {
  min-height: 34px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: 1px solid #c4d0df;
  border-radius: 7px;
  color: var(--space);
  background: white;
  font-size: 10px;
  font-weight: 750;
}

.dictation-review-actions button.confirm {
  border-color: var(--space);
  color: white;
  background: var(--space);
}

.help-agent-badges {
  display: flex !important;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 5px !important;
  font-size: inherit !important;
}

.message-model {
  margin: 6px 0 0 5px;
  display: inline-block;
  color: var(--slate);
  font-size: 8px;
  font-style: italic;
}

.feed-evidence {
  margin: -3px 0 9px;
  display: block;
  color: var(--slate);
  font-size: 8px;
  font-weight: 700;
}

.feed-details .feed-ai-actions {
  margin-bottom: 7px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5px;
}

.feed-details .feed-ai-actions button {
  min-height: 31px;
  justify-content: center;
  border-color: #a9bde0;
  color: var(--space);
  background: #edf3fd;
  text-transform: none;
}

.feed-details .feed-ai-actions button.is-added {
  border-color: rgba(0, 184, 148, 0.35);
  color: #006b59;
  background: #e3f9f4;
}

.feed-details .feed-resolution-actions {
  padding-top: 7px;
  border-top: 1px solid #dce3eb;
}

.audit-drawer {
  width: min(660px, 96vw);
}

.audit-ai-comparison {
  margin-top: 10px;
  overflow: hidden;
  border: 1px solid #d4deea;
  border-radius: 9px;
  background: white;
}

.audit-comparison-row {
  padding: 9px 10px;
}

.audit-comparison-row + .audit-comparison-row {
  border-top: 1px solid #e2e8f0;
}

.audit-comparison-row > span {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--slate);
  font-size: 8px;
  font-weight: 850;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.audit-comparison-row p {
  margin: 5px 0 0;
  display: block;
  color: var(--charcoal);
  font-size: 10px;
  line-height: 1.5;
}

.audit-comparison-row.suggestion {
  border-left: 3px solid var(--azure);
  background: #f5f8ff;
}

.audit-comparison-row.selected {
  border-left: 3px solid var(--success);
  background: #f1fbf8;
}

.audit-agent-meta {
  padding: 7px 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 5px 12px;
  border-top: 1px solid #dbe3ed;
  color: var(--slate);
  background: #f4f7fa;
  font-size: 8px;
}

@media (max-width: 1500px) {
  .omnibox-row {
    grid-template-columns: minmax(320px, 1fr) auto auto;
  }

  .browse-button {
    grid-column: 3;
  }
}

@media (max-width: 900px) {
  .omnibox-row {
    grid-template-columns: 1fr 1fr;
  }

  .omnibox {
    grid-column: 1 / -1;
  }

  .browse-button {
    grid-column: auto;
  }
}

@media (max-width: 620px) {
  .omnibox-row {
    grid-template-columns: 1fr;
  }

  .omnibox {
    grid-column: 1;
  }

  .order-dictation-recording {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .order-dictation-recording > button {
    width: 100%;
    justify-content: center;
  }

  .dictation-review-head {
    flex-direction: column;
  }

  .dictation-review-actions {
    display: grid;
    grid-template-columns: 1fr;
  }

  .feed-details .feed-ai-actions {
    grid-template-columns: 1fr;
  }
}

/* Clear separation between SOAP and order dictation */
.soap-panel-heading {
  height: auto;
  padding-block: 8px;
  gap: 10px;
}

.soap-dictation-control {
  min-height: 34px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.soap-dictation-control button {
  min-height: 34px;
  padding: 0 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: 1px solid #b7c6da;
  border-radius: 7px;
  color: var(--space);
  background: white;
  font-size: 10px;
  font-weight: 800;
}

.soap-dictation-control .soap-dictation-start {
  border-color: var(--space);
  color: white;
  background: var(--space);
}

.soap-dictation-control .soap-dictation-processing {
  color: var(--space);
  background: #eef3fb;
}

.order-dictation-button {
  border-color: var(--azure);
  background: var(--azure);
}

.order-dictation-button:hover {
  border-color: var(--azure-dark);
  background: var(--azure-dark);
}

.section-clear-button {
  min-height: 32px;
  padding: 0 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: 1px solid rgba(229, 72, 77, 0.3);
  border-radius: 8px;
  color: #a9252b;
  background: #fff4f4;
  font-size: 10px;
  font-weight: 750;
}

.clear-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  color: #a9252b;
}

@media (max-width: 1120px) {
  .soap-panel-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .soap-panel-heading > div:last-child {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 620px) {
  .soap-dictation-control {
    width: 100%;
    flex-wrap: wrap;
  }

  .soap-dictation-control .soap-dictation-start {
    width: 100%;
  }
}

/* ============================================================= */
/* Functional demo extensions: tabs, search, dashboard, rounds,  */
/* scribe, catalog search, apply-all, template builder.          */
/* ============================================================= */

/* Clickable logo (brand becomes a button) */
button.brand {
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
}
button.brand:hover .brand-logo {
  box-shadow: 0 0 0 2px var(--aqua);
}

/* Global search dropdown */
.global-search-wrap {
  position: relative;
  justify-self: end;
  width: min(100%, 390px);
}
.global-search-wrap .global-search {
  width: 100%;
}
.search-results {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 60;
  max-height: 380px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid #d3ddea;
  border-radius: 12px;
  background: white;
  box-shadow: var(--shadow-strong);
}
.search-result {
  width: 100%;
  display: grid;
  grid-template-columns: 32px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 8px 9px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  text-align: left;
}
.search-result:hover {
  background: #f1f6ff;
}
.search-result-avatar {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: 9px;
  color: var(--space);
  background: #eaf0fb;
  font-size: 13px;
  font-weight: 800;
}
.search-result-copy {
  min-width: 0;
}
.search-result-copy strong {
  display: block;
  color: var(--ink);
  font-size: 13px;
}
.search-result-copy small {
  display: block;
  margin-top: 2px;
  overflow: hidden;
  color: var(--slate);
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.search-empty {
  padding: 16px 12px;
  color: var(--slate);
  font-size: 12px;
  text-align: center;
}

/* Dashboard */
.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 22px;
}
.metric-card {
  padding: 18px;
  border: 1px solid #d3ddea;
  border-radius: 16px;
  background: white;
  box-shadow: var(--shadow);
}
.metric-icon {
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  margin-bottom: 10px;
  border-radius: 11px;
  color: var(--space);
  background: #eaf1ff;
}
.metric-card strong {
  display: block;
  color: var(--space);
  font-size: 30px;
  letter-spacing: -0.03em;
}
.metric-card span {
  display: block;
  margin-top: 2px;
  color: var(--ink);
  font-size: 13px;
  font-weight: 700;
}
.metric-card small {
  display: block;
  margin-top: 3px;
  color: var(--slate);
  font-size: 11px;
}
.dashboard-columns {
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: 18px;
}
.dash-patient-list {
  padding: 10px;
  display: grid;
  gap: 6px;
}
.dash-patient {
  display: grid;
  grid-template-columns: 12px 1fr auto;
  align-items: center;
  gap: 11px;
  padding: 11px 12px;
  border: 1px solid #e4e9f0;
  border-radius: 11px;
  background: white;
  text-align: left;
}
.dash-patient:hover {
  border-color: var(--azure);
  background: #f8faff;
}
.acuity-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
.acuity-dot.critical { background: var(--error); }
.acuity-dot.watch { background: var(--warning); }
.acuity-dot.stable { background: var(--success); }
.dash-patient-copy strong {
  display: block;
  color: var(--ink);
  font-size: 13px;
}
.dash-patient-copy small {
  display: block;
  margin-top: 2px;
  color: var(--slate);
  font-size: 11px;
}
.activity-list {
  margin: 0;
  padding: 14px 18px 18px 18px;
  list-style: none;
  display: grid;
  gap: 12px;
}
.activity-list li {
  display: flex;
  align-items: center;
  gap: 9px;
  color: var(--charcoal);
  font-size: 12px;
}
.activity-list svg {
  flex: 0 0 auto;
  color: var(--azure-dark);
}

/* Rounds */
.rounds-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}
.round-card {
  padding: 16px;
  border: 1px solid #d3ddea;
  border-radius: 16px;
  background: white;
  box-shadow: var(--shadow);
}
.round-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.round-index {
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  color: white;
  background: var(--space);
  font-size: 12px;
  font-weight: 800;
}
.round-card strong {
  display: block;
  color: var(--space);
  font-size: 15px;
}
.round-card small {
  display: block;
  margin-top: 2px;
  color: var(--slate);
  font-size: 11px;
}
.round-card p {
  margin: 6px 0 0;
  color: var(--charcoal);
  font-size: 12px;
}
.round-card .round-dx {
  color: var(--slate);
}
.round-card .open-chart {
  margin-top: 12px;
  width: 100%;
  justify-content: center;
}

/* SOAP scribe + accept-all buttons */
.soap-dictation-control .soap-scribe-start {
  min-height: 34px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: 1px solid var(--azure);
  border-radius: 7px;
  color: white;
  background: var(--azure);
  font-size: 10px;
  font-weight: 800;
}
.soap-dictation-control .soap-scribe-start:hover {
  background: var(--azure-dark);
}
.note-lang-toggle {
  display: inline-flex;
  align-items: center;
  border: 1px solid #c4d2e5;
  border-radius: 7px;
  overflow: hidden;
}
.note-lang-toggle button {
  min-height: 34px;
  padding: 0 9px;
  border: 0;
  border-radius: 0;
  background: white;
  color: var(--slate);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.02em;
}
.note-lang-toggle button + button {
  border-left: 1px solid #c4d2e5;
}
.note-lang-toggle button.on {
  background: var(--azure);
  color: white;
}
.accept-all-button {
  min-height: 32px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid #00a683;
  border-radius: 8px;
  color: white;
  background: var(--success);
  font-size: 10px;
  font-weight: 800;
}
.new-template-button {
  height: 38px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 11px;
  border: 1px solid #c8d4e3;
  border-radius: 8px;
  color: var(--space);
  background: white;
  font-size: 11px;
  font-weight: 750;
}
.new-template-button:hover {
  border-color: var(--azure);
  color: var(--azure-dark);
}

/* Order catalog autocomplete */
.omnibox-area {
  position: relative;
}
.catalog-suggestions {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 30;
  max-height: 320px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid #b9c8dc;
  border-radius: 12px;
  background: white;
  box-shadow: var(--shadow-strong);
}
.catalog-suggestions-head {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px 8px;
  color: var(--slate);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.catalog-suggestion {
  width: 100%;
  display: grid;
  grid-template-columns: 28px 1fr auto;
  align-items: center;
  gap: 9px;
  padding: 8px 9px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  text-align: left;
}
.catalog-suggestion:hover {
  background: #f1f6ff;
}
.catalog-icon {
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  color: var(--azure-dark);
  background: #eaf1ff;
}
.catalog-copy {
  min-width: 0;
}
.catalog-copy strong {
  display: block;
  color: var(--ink);
  font-size: 12px;
}
.catalog-copy small {
  display: block;
  margin-top: 2px;
  color: var(--slate);
  font-size: 10px;
}
.catalog-lane {
  padding: 4px 7px;
  border-radius: 999px;
  font-size: 8px;
  font-weight: 850;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.catalog-lane.one-day { color: #9a6200; background: #fff4da; }
.catalog-lane.continuous { color: var(--space); background: #eaf1ff; }

/* Heartbeat apply-all */
.feed-heading-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.feed-apply-all {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 9px;
  border: 1px solid var(--azure);
  border-radius: 999px;
  color: var(--azure-dark);
  background: white;
  font-size: 9px;
  font-weight: 850;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.feed-apply-all:hover:not(:disabled) {
  color: white;
  background: var(--azure);
}
.feed-apply-all:disabled {
  opacity: 0.45;
}

/* Clinical viewer empty search state */
.viewer-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 22px 12px;
  color: var(--slate);
  font-size: 12px;
}
.viewer-empty button {
  padding: 5px 10px;
  border: 1px solid #c9d5e5;
  border-radius: 7px;
  color: var(--space);
  background: white;
  font-size: 11px;
  font-weight: 750;
}

/* Template builder modal */
.template-builder {
  width: min(460px, 94vw);
  padding: 20px;
  border-radius: 16px;
  background: white;
  box-shadow: var(--shadow-strong);
  animation: modalIn 0.2s ease both;
}
.template-builder-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.template-builder-head > div {
  display: flex;
  align-items: center;
  gap: 9px;
  color: var(--space);
}
.template-builder-head h2 {
  margin: 0;
  font-size: 18px;
}
.template-builder-head button {
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border: 1px solid #d5deea;
  border-radius: 8px;
  color: var(--slate);
  background: white;
}
.template-builder-hint {
  margin: 0 0 14px;
  color: var(--slate);
  font-size: 12px;
  line-height: 1.5;
}
.template-builder-field span {
  display: block;
  margin-bottom: 6px;
  color: var(--space);
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.template-builder-field input {
  width: 100%;
  height: 42px;
  padding: 0 12px;
  border: 1px solid #c3d0e2;
  border-radius: 9px;
  outline: 0;
  color: var(--ink);
  font-size: 14px;
}
.template-builder-field input:focus {
  border-color: var(--azure);
  box-shadow: 0 0 0 3px rgba(38, 107, 255, 0.1);
}
.template-builder-summary {
  display: flex;
  gap: 14px;
  margin-top: 12px;
}
.template-builder-summary span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--slate);
  font-size: 11px;
}
.template-builder-actions {
  margin-top: 18px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

@media (max-width: 1120px) {
  .metric-grid { grid-template-columns: repeat(2, 1fr); }
  .dashboard-columns { grid-template-columns: 1fr; }
}
@media (max-width: 560px) {
  .metric-grid { grid-template-columns: 1fr; }
}

/* Help Desk suggested prompt chips */
.suggested-questions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 12px 10px;
}
.suggested-questions .suggested-question {
  margin: 0;
  max-width: none;
  padding: 7px 10px;
  border: 1px solid #c4d2e5;
  border-radius: 999px;
  color: var(--space);
  background: #f1f6ff;
  font-size: 10px;
  font-weight: 700;
}
.suggested-questions .suggested-question:hover:not(:disabled) {
  color: white;
  background: var(--space);
}
.suggested-questions .suggested-question:disabled {
  opacity: 0.5;
}

/* Order approve/save footer */
.order-footer {
  padding: 13px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-top: 1px solid #d6deea;
  background: #eef2f7;
}
.order-footer-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--slate);
  font-size: 12px;
  font-weight: 650;
}
.approve-orders-button {
  min-width: 210px;
  padding: 10px 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 0;
  border-radius: 8px;
  color: white;
  background: var(--space);
  box-shadow: 0 6px 15px rgba(15, 53, 117, 0.18);
  font-size: 12px;
  font-weight: 800;
}
.approve-orders-button:hover:not(:disabled) {
  background: var(--space-deep);
}
.approve-orders-button.is-confirmed {
  background: #007e68;
}
.approve-orders-button:disabled {
  opacity: 0.45;
}

/* Save & return to ward (order header) */
.save-return-button {
  height: 38px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 13px;
  border: 0;
  border-radius: 8px;
  color: white;
  background: var(--success);
  box-shadow: 0 4px 12px rgba(0, 184, 148, 0.25);
  font-size: 12px;
  font-weight: 800;
}
.save-return-button:hover {
  background: #00a382;
}

/* Editable template builder (large) */
.template-builder-lg {
  width: min(760px, 96vw);
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}
.tb-scroll {
  overflow-y: auto;
  padding-right: 4px;
  margin: 6px 0;
}
.tb-section {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid var(--platinum);
}
.tb-section h3 {
  margin: 0 0 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--space);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.tb-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.tb-add-order {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px;
  border: 1px solid var(--azure);
  border-radius: 7px;
  color: var(--azure-dark);
  background: white;
  font-size: 11px;
  font-weight: 750;
}
.tb-add-order:hover {
  color: white;
  background: var(--azure);
}
.tb-soap-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.tb-soap-field span {
  display: block;
  margin-bottom: 5px;
  color: var(--space);
  font-size: 11px;
  font-weight: 700;
}
.tb-soap-field textarea {
  width: 100%;
  padding: 8px 10px;
  resize: vertical;
  border: 1px solid #cfd8e5;
  border-radius: 8px;
  outline: 0;
  color: var(--ink);
  font-size: 13px;
  line-height: 1.5;
}
.tb-soap-field textarea:focus {
  border-color: var(--azure);
  box-shadow: 0 0 0 3px rgba(38, 107, 255, 0.1);
}
.tb-order-list {
  display: grid;
  gap: 8px;
}
.tb-empty {
  margin: 0;
  padding: 12px;
  color: var(--slate);
  font-size: 12px;
  text-align: center;
  background: #f6f8fb;
  border-radius: 8px;
}
.tb-order-row {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1.4fr) 110px 130px auto auto;
  gap: 6px;
  align-items: center;
}
.tb-order-row input,
.tb-order-row select {
  height: 34px;
  padding: 0 8px;
  border: 1px solid #cfd8e5;
  border-radius: 7px;
  outline: 0;
  color: var(--ink);
  background: white;
  font-size: 12px;
}
.tb-order-row input:focus,
.tb-order-row select:focus {
  border-color: var(--azure);
}
.tb-stat {
  height: 34px;
  padding: 0 8px;
  border: 1px solid #cbd6e3;
  border-radius: 7px;
  color: var(--slate);
  background: white;
  font-size: 9px;
  font-weight: 850;
}
.tb-stat.on {
  border-color: rgba(229, 72, 77, 0.4);
  color: white;
  background: var(--error);
}
.tb-remove {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border: 1px solid #d9e0e9;
  border-radius: 7px;
  color: var(--slate);
  background: #f8fafc;
}
.tb-remove:hover {
  border-color: rgba(229, 72, 77, 0.4);
  color: var(--error);
}
.template-builder-lg .template-builder-actions {
  align-items: center;
  border-top: 1px solid var(--platinum);
  padding-top: 14px;
}
.tb-count {
  margin-right: auto;
  color: var(--slate);
  font-size: 11px;
}

@media (max-width: 620px) {
  .tb-soap-grid { grid-template-columns: 1fr; }
  .tb-order-row { grid-template-columns: 1fr 1fr; }
  .order-footer { flex-direction: column; align-items: stretch; }
  .approve-orders-button { width: 100%; }
}

/* ============================================================= */
/* Auto-pilot mode: AI-authored visual treatment (company CI).   */
/* ============================================================= */

.autopilot-toggle {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 32px;
  padding: 0 11px;
  border: 1px solid #c4d2e5;
  border-radius: 999px;
  color: var(--slate);
  background: white;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.autopilot-toggle.on {
  border-color: transparent;
  color: white;
  background: linear-gradient(120deg, var(--azure), var(--space));
  box-shadow: 0 4px 12px rgba(38, 107, 255, 0.28);
}

/* AI-authored SOAP card */
.soap-card.ai-authored {
  border-color: rgba(38, 107, 255, 0.5);
  background: linear-gradient(180deg, rgba(38, 107, 255, 0.06), rgba(255, 255, 255, 0.9));
}
.soap-card.ai-authored textarea {
  color: var(--azure-dark);
  font-style: italic;
}
.ai-label.autopilot {
  color: white;
  background: linear-gradient(120deg, var(--azure), var(--space));
}

/* Generating shimmer while auto-pilot fills a field */
.soap-card.ai-generating {
  position: relative;
}
.soap-card.ai-generating::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background: linear-gradient(100deg, transparent 20%, rgba(38, 107, 255, 0.14) 45%, rgba(0, 209, 178, 0.16) 55%, transparent 80%);
  background-size: 220% 100%;
  animation: aiShimmer 1.2s ease-in-out infinite;
}
@keyframes aiShimmer {
  0% { background-position: 180% 0; }
  100% { background-position: -80% 0; }
}
.ai-generating-overlay {
  position: absolute;
  top: 10px;
  right: 12px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 9px;
  border-radius: 999px;
  color: white;
  background: linear-gradient(120deg, var(--azure), var(--aqua, #00d1b2));
  font-size: 10px;
  font-weight: 800;
  box-shadow: 0 4px 10px rgba(38, 107, 255, 0.3);
  animation: aiPulse 1s ease-in-out infinite;
}
@keyframes aiPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

/* AI-authored order chip */
.order-chip.ai-authored {
  position: relative;
  border-color: rgba(38, 107, 255, 0.45);
  background: linear-gradient(180deg, rgba(38, 107, 255, 0.05), white);
}
.order-ai-badge {
  position: absolute;
  top: -7px;
  left: 10px;
  right: auto;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px;
  border-radius: 999px;
  border: 1px solid white;
  box-shadow: 0 1px 4px rgba(15, 53, 117, 0.18);
  color: white;
  background: linear-gradient(120deg, var(--azure), var(--space));
  font-size: 8px;
  font-weight: 850;
  letter-spacing: 0.04em;
}

/* --- Scribe / AI-authored polish (#3, #4) -------------------------------- */
/* Keep the AI status pill on a single line and never let it get squeezed, so
   it renders consistently across all four SOAP cards regardless of width. */
.soap-card-head {
  flex-wrap: nowrap;
  gap: 8px;
}
.soap-card-head > div:first-child {
  min-width: 0; /* allow the title/hint to shrink so the pill stays intact */
}
.soap-card-head .ai-label {
  white-space: nowrap;
  flex-shrink: 0;
}
/* Let the SOAP box grow to fit inserted (scribe/accept) text instead of
   capping at a fixed height and showing an inner scrollbar. */
.soap-card textarea {
  max-height: none;
  overflow-y: hidden;
}

/* --- Order drag-and-drop between lanes ----------------------------------- */
.order-chip.is-draggable {
  cursor: grab;
}
.order-chip.is-draggable:active {
  cursor: grabbing;
}
.lane-body {
  min-height: 44px;
  border-radius: 8px;
  transition: background 0.12s ease, box-shadow 0.12s ease;
}
.lane-body.drag-over {
  background: rgba(38, 107, 255, 0.06);
  box-shadow: inset 0 0 0 2px rgba(38, 107, 255, 0.45);
}

/* --- TMT code chip on drug orders ---------------------------------------- */
.order-chip-meta .order-tmt {
  padding: 1px 6px;
  border-radius: 5px;
  border: 1px solid #d6deea;
  background: #eef3fb;
  color: var(--azure-dark);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.02em;
}

/* --- Interface language toggle (top nav) --------------------------------- */
.nav-lang-toggle {
  display: inline-flex;
  align-items: center;
  border: 1px solid #c4d2e5;
  border-radius: 7px;
  overflow: hidden;
}
.nav-lang-toggle button {
  min-height: 30px;
  padding: 0 9px;
  border: 0;
  background: white;
  color: var(--slate);
  font-size: 11px;
  font-weight: 800;
}
.nav-lang-toggle button + button {
  border-left: 1px solid #c4d2e5;
}
.nav-lang-toggle button.on {
  background: var(--azure);
  color: white;
}

/* --- Per-lane order search (#3) + bundle add-all row (#2) ----------------- */
.lane-search {
  position: relative;
  margin-bottom: 8px;
}
.lane-search-box {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 8px;
  min-height: 32px;
  border: 1px dashed #c4d2e5;
  border-radius: 7px;
  color: var(--slate);
  background: #f8fafc;
}
.lane-search-box input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--ink);
  font-size: 11px;
}
.lane-search-results {
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  margin-top: 4px;
  max-height: 230px;
  overflow-y: auto;
  border: 1px solid #d6deea;
  border-radius: 8px;
  background: white;
  box-shadow: 0 10px 26px rgba(15, 53, 117, 0.16);
}
.catalog-suggestion.bundle-add {
  background: rgba(38, 107, 255, 0.06);
}
.catalog-suggestion.bundle-add strong {
  color: var(--azure-dark);
}

/* ===========================================================
   Auditor Agent — live documentation-check panel (right rail)
   =========================================================== */
.auditor-panel {
  background: var(--white);
  border: 1px solid var(--platinum);
  border-radius: 14px;
  box-shadow: var(--shadow);
  padding: 16px 16px 12px;
  margin-bottom: 16px;
}
.auditor-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}
.auditor-head .a-ic {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: var(--surface-blue);
  color: var(--space);
  flex: none;
}
.auditor-head h3 {
  margin: 0;
  font-size: 15px;
  color: var(--ink);
  font-weight: 650;
}
.auditor-count {
  margin-left: auto;
  font-size: 12px;
  font-weight: 700;
  color: var(--white);
  background: var(--error);
  border-radius: 999px;
  padding: 2px 9px;
  min-width: 22px;
  text-align: center;
}
.auditor-count.clear {
  background: var(--success);
}
.auditor-sub {
  font-size: 12px;
  color: var(--slate);
  margin: 0 0 12px;
}
.auditor-empty {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 13px;
  color: #0a7d67;
  background: rgba(0, 184, 148, 0.08);
  border: 1px solid rgba(0, 184, 148, 0.25);
  border-radius: 10px;
  padding: 12px 13px;
}
.aud-finding {
  border: 1px solid var(--platinum);
  border-radius: 11px;
  padding: 12px 13px;
  margin-bottom: 9px;
  background: var(--white);
}
.aud-finding.sev-high {
  border-left: 3px solid var(--error);
}
.aud-finding.sev-medium {
  border-left: 3px solid var(--warning);
}
.aud-finding.sev-low {
  border-left: 3px solid var(--slate);
}
.aud-row1 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.aud-sev {
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 5px;
}
.aud-sev.high {
  background: rgba(229, 72, 77, 0.12);
  color: var(--error);
}
.aud-sev.medium {
  background: rgba(245, 158, 11, 0.14);
  color: #9a6606;
}
.aud-sev.low {
  background: var(--platinum);
  color: var(--slate);
}
.aud-flag {
  font-size: 10px;
  font-weight: 600;
  color: var(--slate);
}
.aud-dismiss {
  margin-left: auto;
  background: transparent;
  border: none;
  color: var(--mist);
  cursor: pointer;
  padding: 2px;
  border-radius: 5px;
  display: grid;
  place-items: center;
}
.aud-dismiss:hover {
  color: var(--slate);
  background: var(--surface);
}
.aud-title {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--ink);
  line-height: 1.4;
  margin: 0 0 5px;
}
.aud-detail {
  font-size: 12.5px;
  color: var(--charcoal);
  margin: 0 0 8px;
  line-height: 1.5;
}
.aud-gap {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
  color: var(--error);
  background: rgba(229, 72, 77, 0.06);
  border-radius: 7px;
  padding: 7px 9px;
  margin: 0 0 8px;
}
.aud-finding.sev-medium .aud-gap {
  color: #9a6606;
  background: rgba(245, 158, 11, 0.08);
}
.aud-finding.sev-low .aud-gap {
  color: var(--slate);
  background: var(--surface);
}
.aud-rec {
  font-size: 12.5px;
  color: var(--ink);
  margin: 0 0 10px;
}
.aud-rec b {
  color: var(--space);
}
.aud-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.aud-fix {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--white);
  background: var(--space);
  border: none;
  border-radius: 8px;
  padding: 7px 12px;
  cursor: pointer;
}
.aud-fix:hover {
  background: var(--space-deep);
}
.aud-ground {
  font-size: 11px;
  color: var(--slate);
}
.aud-ground b {
  color: var(--azure-dark);
  font-weight: 600;
}

/* --- Sign-in (AuthPanel.tsx) ---------------------------------------------- */
.auth-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 30px;
  padding: 0 10px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 7px;
  background: transparent;
  color: white;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.auth-chip:hover,
.auth-chip:focus-visible {
  background: rgba(255, 255, 255, 0.12);
}
.auth-chip-primary {
  border-color: var(--azure);
  background: var(--azure);
}
.auth-chip-primary:hover,
.auth-chip-primary:focus-visible {
  background: var(--azure-dark);
}
.auth-account {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.user-chip .auth-email {
  display: inline;
  max-width: 160px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  color: rgba(255, 255, 255, 0.8);
  font-size: 11px;
}
.auth-backdrop {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(10, 40, 93, 0.45);
}
.auth-page {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 16px;
  background: var(--surface);
}
.auth-card {
  width: min(400px, 100%);
  padding: 24px;
  border: 1px solid var(--platinum);
  border-radius: 14px;
  background: var(--white);
  color: var(--ink);
  box-shadow: var(--shadow-strong);
  white-space: normal;
}
.auth-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}
.auth-card-head h1,
.auth-card-head h2 {
  margin: 0;
  color: var(--space);
  font-size: 18px;
  font-weight: 800;
}
.auth-brand {
  justify-content: flex-start;
}
.auth-close {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: var(--slate);
  cursor: pointer;
}
.auth-close:hover,
.auth-close:focus-visible {
  background: var(--surface);
}
.auth-form {
  display: grid;
  gap: 12px;
}
.auth-form label {
  display: grid;
  gap: 5px;
}
.auth-form label > span {
  display: block;
  margin: 0;
  color: var(--charcoal);
  font-size: 12px;
  font-weight: 700;
}
.auth-form input {
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid var(--mist);
  border-radius: 8px;
  background: var(--white);
  color: var(--ink);
  font-size: 14px;
}
.auth-form input:focus {
  outline: 2px solid var(--azure);
  outline-offset: 1px;
  border-color: var(--azure);
}
.auth-form small {
  color: var(--slate);
  font-size: 11px;
}
.auth-hint,
.auth-notice,
.auth-error {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
}
.auth-hint {
  color: var(--charcoal);
}
.auth-notice {
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--surface-blue);
  color: var(--space);
}
.auth-error {
  padding: 8px 10px;
  border-radius: 8px;
  background: #fdecec;
  color: #a4161a;
}
.auth-submit {
  min-height: 42px;
  border: 0;
  border-radius: 8px;
  background: var(--azure);
  color: white;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
}
.auth-submit:hover,
.auth-submit:focus-visible {
  background: var(--azure-dark);
}
.auth-submit:disabled {
  opacity: 0.6;
  cursor: progress;
}
.auth-links {
  display: grid;
  gap: 6px;
  font-size: 12px;
  color: var(--charcoal);
}
.auth-links button {
  padding: 0;
  border: 0;
  background: none;
  color: var(--azure-dark);
  font: inherit;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}
.auth-links button:hover,
.auth-links button:focus-visible {
  text-decoration: underline;
}
.auth-muted {
  color: var(--slate);
}
